import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import Calendar from "../../pages/advisor-portal/Calendar";
import Messages from "../../pages/advisor-portal/Messages";
import AddClientModal from "../../components/domains/advisor-portal/add-new-client/AddClientModal";
import Allocations from "../../pages/portfolio-management/Allocations";
import MarketNews from "../../pages/portfolio-management/MarketNews";
import MarketResearch from "../../pages/portfolio-management/MarketResearch";
import PortfolioReports from "../../pages/portfolio-management/PortfolioReports";
import TradingRebalancing from "../../pages/portfolio-management/TradingRebalancing";
import Billing from "../../pages/portfolio-management/Billing";
import Clients from "../../pages/advisor-portal/client-database/Clients";
import Settings from "../../pages/administrative/Settings";
import FirmSettings from "../../pages/administrative/FirmSettings/FirmSettings";
import Email from "../../pages/advisor-portal/Email";
import WithdrawalsDistributions from "../../pages/portfolio-management/WithdrawalsDistributions";
import Banking from "../../pages/financial-planning/Banking";
import TaxPlanning from "../../pages/financial-planning/tax-planning/TaxPlanning";
import EstatePlanning from "../../pages/financial-planning/estate-planning/EstatePlanning";
import FinancialCalculators from "../../pages/financial-planning/calculators/FinancialCalculators";
import AnalysisReports from "../../pages/financial-planning/AnalysisReports";
import RetirementJourney from "../../pages/financial-planning/RetirementJourney";
import Insurance from "../../pages/financial-planning/insurance/Insurance";
import Events from "../../pages/advisor-portal/events/Events";
import SocialMediaMarketing from "../../pages/marketing-and-sales/SocialMediaMarketing";
import SalesCoach from "../../pages/marketing-and-sales/SalesCoach";
import Campaigns from "../../pages/marketing-and-sales/Campaigns";
import DesignHub from "../../pages/marketing-and-sales/DesignHub";
import MarketingAnalytics from "../../pages/marketing-and-sales/MarketingAnalytics";
import InsuranceCoverage from "../../pages/financial-planning/insurance/InsuranceCoverage";
import FinancialLiteracy from "../education-and-licensing/FinancialLiteracy";
import FinancialNews from "../education-and-licensing/FinancialNews";
import CurrentLicenses from "../education-and-licensing/CurrentLicenses";
import CERequirements from "../education-and-licensing/CERequirements";
import FirmOverview from "../administrative/FirmOverview/FirmOverview";
import CompanyStructure from "../administrative/CompanyStructure/CompanyStructure";
import Branding from "../administrative/Branding/Branding";
import ComplianceCenter from "../administrative/ComplianceCenter/ComplianceCenter";
import PayrollTax from "../administrative/PayrollAndTax/PayrollTax";
import FirmAnalytics from "../administrative/FirmAnalytics/FirmAnalytics";
import SubPageLayout from "../../components/shared/SubPageLayout";
import { useEmail } from "../../contexts/EmailContext"; // Updated path
import { useAuth } from "../../contexts/AuthContext";
import SidebarTemplate from "../../components/shared/SidebarTemplate";
import TaskRecommendations from "../advisor-portal/client-database/TaskRecommendations";
import Card from "../../components/shared/Card";
import DailySummary from "../../components/domains/advisor-portal/dashboard/DailySummary";
import QuickActions from "../../components/domains/advisor-portal/dashboard/QuickActions";
import Schedule from "../../components/domains/advisor-portal/dashboard/Schedule";
import { useEvents, EVENT_STATUS } from "../../contexts/EventContext"; // Add this import

import { useDemo } from "../../contexts/DemoContext";
import { demoMeetingDetails, demoFormData, demoPipelineData } from "../../demo/briefingRoomData";
import {
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { formatTimeRange } from "../../utils/formatTimeRange";
import { cleanEventTitle } from "../../utils/cleanEventTitle";
import EventModal from "../../components/shared/calendar/EventModal";
import {
  format,
  isToday,
  isTomorrow,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  roundToNearestMinutes,
  addHours,
  addDays,
  addWeeks,
  isSameDay,
} from "date-fns";
import MeetingBriefingRoom from "../../components/domains/advisor-portal/MeetingBriefingRoom";
import { useTheme } from "../../contexts/ThemeContext";

import logger from '../../utils/logger';
import SpotlightOverlay from '../../components/shared/SpotlightOverlay';
import DemoSelectionModal from "../../components/shared/DemoSelectionModal";

// Add these constants at the top of the file, after the imports
const CARD_BASE_STYLE = "bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 dark:bg-surface-raised dark:border-neutral-700 dark:shadow-none";
const CARD_HEADER_STYLE = "text-lg font-semibold text-neutral-900 dark:text-neutral-100";
const SECTION_PADDING = "p-5";
const QUICK_ACTION_BUTTON = "transition-all duration-200 hover:shadow-md hover:scale-[1.02] cursor-pointer bg-opacity-75 hover:bg-opacity-100 dark:shadow-none";

// Add this function after imports, before the component
const __isCompletedLocal = (ev) => {
  try {
    const key = `demo:schedule:completed:${ev?.id || (ev?.title + "|" + ev?.startTime)}`;
    return localStorage.getItem(key) === "1";
  } catch { return false; }
};
const transformEventsForSchedule = (events) => {
  logger.info("HomeDashboard - Transforming events:", events);
  const transformed = events.map((event) => ({
    id: event.id,
    title: event.title,
    startTime: event.start,
    endTime: event.end,
    type: event.extendedProps?.type?.toLowerCase() || "meeting",
    location: event.extendedProps?.location,
    attendees: event.extendedProps?.attendees?.map((a) => a.name),
  }));
  logger.info("HomeDashboard - Transformed events:", transformed);
  return transformed;
};

// Safety switch for legacy restore logic
const SKIP_LEGACY_RESTORE = localStorage.getItem("skipLegacyRestore") === "true";

const HomeDashboard = () => {
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showEventCreator, setShowEventCreator] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [calendarView, setCalendarView] = useState("dayGridMonth");
  const [showSpotlight, setShowSpotlight] = useState(true);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [showTradeExecution, setShowTradeExecution] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [suggestions, setSuggestions] = useState([
    {
      id: '1',
      text: "Rebalance portfolio to reduce risk 5 points for 100% portfolio alignment",
      tag: { text: "Urgent", color: "red" },
      isChecked: false,
      subtasks: [
        { id: 'st1', text: "Assess current asset allocation", completed: false },
        { id: 'st2', text: "Execute trades to increase Risk Profile by 5 points", completed: false },
        { id: 'st3', text: "Verify alignment with client risk profile", completed: false }
      ]
    },
    {
      id: '2',
      text: "Schedule annual portfolio review for Q3 planning",
      tag: { text: "Deadline Upcoming", color: "yellow" },
      isChecked: false,
      subtasks: [
        { id: 'st4', text: "Send meeting invitation to client", completed: false },
        { id: 'st5', text: "Prepare portfolio performance report", completed: false },
        { id: 'st6', text: "Review long-term financial goals", completed: false }
      ]
    },
    {
      id: '3',
      text: "Contact client regarding RMD for IRA account",
      tag: { text: "Recommended", color: "blue" },
      isChecked: false,
      subtasks: [
        { id: 'st7', text: "Calculate minimum distribution amount", completed: false },
        { id: 'st8', text: "Email client with withdrawal options", completed: false },
        { id: 'st9', text: "Confirm compliance with IRS regulations", completed: false }
      ]
    }
  ]);
  const [loadingAISuggestions, setLoadingAISuggestions] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useEmail();
  const { user } = useAuth();
  const { events, tasks, addEvent, updateEvent } = useEvents();
  const { isDemoMode, selectedJourney, startDemo, resetDemo, onAction, demoStep, setSelectedJourney } = useDemo();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [currentView, setCurrentView] = useState("home");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const demoMeetingRef = useRef(null);
  const [showBriefingRoom, setShowBriefingRoom] = useState(false);
  const isExistingClientDemo = isDemoMode && selectedJourney === "Existing Client";
  const [showDemoModal, setShowDemoModal] = useState(isDemoMode && !selectedJourney);

  // Calculate today's meetings count dynamically
  const todaysMeetings = useMemo(() => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return isToday(eventDate) && 
             !event.isTask && 
             event.extendedProps?.status !== EVENT_STATUS.COMPLETED;
    }).length;
  }, [events]);

  // Function to mark a meeting as completed
  const markMeetingComplete = useCallback((meetingId) => {
    const meeting = events.find(e => e.id === meetingId);
    if (meeting) {
      updateEvent({
        ...meeting,
        extendedProps: {
          ...meeting.extendedProps,
          status: EVENT_STATUS.COMPLETED,
        },
      });
    }
  }, [events, updateEvent]);

  // Show demo modal when requested (stop-demo/logout), or when demo mode is on without a journey
  useEffect(() => {
    const forceSelect = (() => {
      try { return localStorage.getItem('demo_force_select') === 'true'; } catch (_) { return false; }
    })();

    if (forceSelect || location.state?.showDemoSelect || (isDemoMode && !selectedJourney)) {
      setShowDemoModal(true);
      // Clear flags so it doesn't persist
      if (forceSelect) {
        try { localStorage.removeItem('demo_force_select'); } catch (_) {}
      }
      if (location.state?.showDemoSelect) {
        const newState = { ...location.state };
        delete newState.showDemoSelect;
        window.history.replaceState(newState, '', location.pathname);
      }
    }
  }, [isDemoMode, selectedJourney, location.state, location.pathname]);

  // Get stored user data
  const storedUser = JSON.parse(localStorage.getItem("user"));

  // Update the advisor name logic to use camelCase
  const advisorFirstName = user?.is_demo
    ? "Advisor"
    : user?.firstName ||
    JSON.parse(localStorage.getItem("user"))?.firstName ||
    "Advisor";

  // Get time of day greeting
  const getTimeOfDayGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  // Task-related handlers
  const handleSuggestionCheck = (taskId) => {
    setSuggestions(prev => prev.map(task =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const generateNewSuggestions = async () => {
    setLoadingAISuggestions(true);
    // Simulate API delay
    setTimeout(() => {
      const newSuggestions = [
        {
          id: '1',
          text: "Review tax efficiency of current asset location strategy",
          tag: { text: "Recommended", color: "blue" },
          isChecked: false,
          subtasks: [
            { id: 'st1', text: "Analyze current tax implications", completed: false },
            { id: 'st2', text: "Identify optimization opportunities", completed: false },
            { id: 'st3', text: "Prepare tax-efficient reallocation plan", completed: false }
          ]
        },
        {
          id: '2',
          text: "Introduce new ESG investment options to align with client values",
          tag: { text: "Recommended", color: "blue" },
          isChecked: false,
          subtasks: [
            { id: 'st4', text: "Research ESG investment options", completed: false },
            { id: 'st5', text: "Prepare ESG portfolio proposal", completed: false },
            { id: 'st6', text: "Schedule client presentation", completed: false }
          ]
        },
        {
          id: '3',
          text: "Send email about upcoming tax loss harvesting opportunity",
          tag: { text: "Deadline Upcoming", color: "yellow" },
          isChecked: false,
          subtasks: [
            { id: 'st7', text: "Identify tax loss candidates", completed: false },
            { id: 'st8', text: "Calculate potential tax savings", completed: false },
            { id: 'st9', text: "Draft client communication", completed: false }
          ]
        }
      ];
      setSuggestions(newSuggestions);
      setLoadingAISuggestions(false);
    }, 1500);
  };

  const handleViewDetails = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  const handleComplete = (taskId) => {
    setSuggestions(prev => prev.map(task =>
      task.id === taskId ? { ...task, completed: true } : task
    ));
  };

  const handleOpenTradeExecution = () => {
    setShowTradeExecution(true);
  };

  const handleCompleteTradeExecution = () => {
    setShowTradeExecution(false);
    // Additional logic for completing trade execution
  };

  const handleSubtaskUpdate = (taskId, completionStatus) => {
    setSuggestions(prev => prev.map(task =>
      task.id === taskId ? {
        ...task, subtasks: task.subtasks.map(st =>
          st.id === completionStatus.id ? { ...st, completed: completionStatus.completed } : st
        )
      } : task
    ));
  };

  const handleAddNote = (taskId, note) => {
    setSuggestions(prev => prev.map(task =>
      task.id === taskId ? { ...task, notes: [...(task.notes || []), note] } : task
    ));
  };

  const handleSetReminder = (taskId, date) => {
    setSuggestions(prev => prev.map(task =>
      task.id === taskId ? { ...task, reminder: date } : task
    ));
  };

  // Call-related handlers
  const handleStartCall = () => {
    setShowCallModal(true);
  };


  // Household-related handlers
  const getHouseholdName = () => {
    // TODO: Implement household name retrieval
    return "Sample Household";
  };

  // Handle route protection
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }
  }, [navigate]);

  // Render the dashboard home content
  return (
    <div className="h-full overflow-auto hide-scrollbar px-4 pt-4 pb-4">
      {/* Demo Selection Modal */}
      {showDemoModal && (
        <DemoSelectionModal
          isOpen={showDemoModal}
          onSelect={(journeyId) => {
            // Call startDemo to set up demo state and spotlight, but it won't start transcript
            startDemo(journeyId);
            setShowDemoModal(false);
          }}
          onClose={() => setShowDemoModal(false)}
          onResetDemo={(demoId) => {
            resetDemo(demoId);
          }}
        />
      )}

      <div className="space-y-2.5">
        {/* Top Section with Daily Summary and Quick Actions */}
        <div className="grid grid-cols-12 gap-4">
          {/* Daily Summary Card - 6 columns (1/2) */}
          <div className="col-span-6">
            <DailySummary
              meetings={todaysMeetings}
              tasks={tasks.length}
              marketTrend="down"
              marketReason="interest rate concerns, and continued uncertainty surrounding a potential AI bubble."
              advisorFirstName={advisorFirstName}
            />
          </div>

          {/* Quick Actions Card - 6 columns (1/2) */}
          <div className="col-span-6">
            <QuickActions
              actions={[
                {
                  id: "email",
                  icon: "📧",
                  label: "Email",
                  count: unreadCount || 5,
                  route: "/advisor-portal/email",
                },
                {
                  id: "messages",
                  icon: "💬",
                  label: "Messages",
                  count: 3,
                  route: "/advisor-portal/messages",
                },
                {
                  id: "events",
                  icon: "📅",
                  label: "Events",
                  count: 2,
                  route: "/advisor-portal/events",
                },
                {
                  id: "sales-coach",
                  icon: "🎯",
                  label: "Sales Coach",
                  count: 0,
                  route: "/marketing-sales/sales-coach",
                },
                {
                  id: "client-database",
                  icon: "👥",
                  label: "Client Database",
                  count: 1,
                  route: "/advisor-portal/client-database",
                },
                {
                  id: "market-news",
                  icon: "📈",
                  label: "Market News",
                  count: 4,
                  route: "/portfolio-management/market-news",
                },
                {
                  id: "research",
                  icon: "🔍",
                  label: "Market Research",
                  count: 0,
                  route: "/portfolio-management/market-research",
                },
                {
                  id: "allocations",
                  label: "Allocations",
                  count: 2,
                  route: "/portfolio-management/firm-allocations",
                },
                {
                  id: "financial-news",
                  icon: "📰",
                  label: "Financial News",
                  count: 7,
                  route: "/education-licensing/financial-news",
                },
                {
                  id: "compliance",
                  icon: "🛡️",
                  label: "Compliance",
                  count: 1,
                  route: "/administrative/compliance-center",
                },
              ]}
              onCustomize={() => console.log("Customize clicked")}
            />
          </div>
        </div>

        {/* Schedule Section */}
        <Schedule
          events={transformEventsForSchedule(events)}
          view={calendarView}
          onViewChange={(newView) => {
            setCalendarView(newView);
          }}
          onAddEvent={() => {
            setShowEventCreator(true);
          }}
        />

        {/* Task Recommendations Section */}
        <TaskRecommendations
          suggestions={suggestions}
          onSuggestionCheck={handleSuggestionCheck}
          onGenerateNew={generateNewSuggestions}
          loading={loadingAISuggestions}
          onViewDetails={handleViewDetails}
          onComplete={handleComplete}
          onOpenTradeExecution={handleOpenTradeExecution}
          onCompleteTradeExecution={handleCompleteTradeExecution}
          onSubtaskUpdate={handleSubtaskUpdate}
          onAddNote={handleAddNote}
          onSetReminder={handleSetReminder}
          showTaskDetail={showTaskDetail}
          setShowTaskDetail={setShowTaskDetail}
          showTradeExecution={showTradeExecution}
          setShowTradeExecution={setShowTradeExecution}
          selectedTask={selectedTask}
        />

      </div>

      {/* Modals */}
      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
      />

      {showEmailComposer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-default dark:bg-surface-default dark:bg-gray-800 rounded-lg w-full max-w-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700 bg-surface-default dark:bg-surface-default dark:bg-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">
                Compose Email
              </h2>
              <button
                onClick={() => setShowEmailComposer(false)}
                className="text-gray-500 dark:text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4 bg-surface-default dark:bg-surface-default dark:bg-gray-700">
              <input
                type="text"
                placeholder="To"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                         text-gray-900 dark:text-gray-100 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         bg-surface-default dark:bg-surface-default dark:bg-gray-800"
              />
              <input
                type="text"
                placeholder="Subject"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                         text-gray-900 dark:text-gray-100 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         bg-surface-default dark:bg-surface-default dark:bg-gray-800"
              />
              <textarea
                placeholder="Message"
                rows={6}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg resize-none 
                         text-gray-900 dark:text-gray-100 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         bg-surface-default dark:bg-surface-default dark:bg-gray-800"
              />
              <div className="flex justify-between items-center">
                <button className="text-gray-600 dark:text-gray-400 dark:text-gray-300 hover:text-gray-800 dark:text-gray-200 dark:hover:text-gray-100 flex items-center">
                  <svg
                    className="w-5 h-5 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" />
                  </svg>
                  Attach
                </button>
                <div className="space-x-2">
                  <button
                    onClick={() => setShowEmailComposer(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 
                             hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg"
                  >
                    Save Draft
                  </button>
                  <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]">
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEventCreator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-default dark:bg-surface-default dark:bg-gray-800 rounded-lg w-full max-w-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 dark:border-gray-700 bg-surface-default dark:bg-surface-default dark:bg-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">
                Create Event
              </h2>
              <button
                onClick={() => setShowEventCreator(false)}
                className="text-gray-500 dark:text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4 bg-surface-default dark:bg-surface-default dark:bg-gray-700">
              <input
                type="text"
                placeholder="Event Title"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                         text-gray-900 dark:text-gray-100 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         bg-surface-default dark:bg-surface-default dark:bg-gray-800"
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
                    Start Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                             text-gray-900 dark:text-gray-100 dark:text-gray-100 
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                             bg-surface-default dark:bg-surface-default dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-1">
                    End Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                             text-gray-900 dark:text-gray-100 dark:text-gray-100 
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                             bg-surface-default dark:bg-surface-default dark:bg-gray-800"
                  />
                </div>
              </div>
              <textarea
                placeholder="Event Description"
                rows={4}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg resize-none 
                         text-gray-900 dark:text-gray-100 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                         bg-surface-default dark:bg-surface-default dark:bg-gray-800"
              />
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 dark:text-blue-400"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300">
                      All Day Event
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 dark:text-blue-400"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 dark:text-gray-300">
                      Repeat
                    </span>
                  </label>
                </div>
                <div className="space-x-2">
                  <button
                    onClick={() => setShowEventCreator(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 
                             hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button className="px-4 py-2 bg-[#029831] text-white rounded-lg hover:bg-[#018529]">
                    Create Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MeetingBriefingRoom */}
      <div
        className="relative"
        style={{ zIndex: showBriefingRoom ? 20000 : "auto" }}
      >
        <MeetingBriefingRoom
          isOpen={showBriefingRoom}
          onClose={() => {
            if (!isExistingClientDemo) {
              setShowBriefingRoom(false);
            }
          }}
          meetingDetails={demoMeetingDetails}
          formData={demoFormData}
          pipelineData={demoPipelineData}
          getHouseholdName={getHouseholdName}
          onStartCall={handleStartCall}
        />
      </div>

      {/* Add SpotlightOverlay */}
      {isExistingClientDemo && showSpotlight && demoStep === 0 && (
        <SpotlightOverlay
          targetSelector="[data-demo-target='guided-demo-event']"
          onSpotlightClick={() => {
            navigate("/advisor-portal/client-database/household-profile/253/ar-profile/2025/1");
            onAction({ type: 'START_DEMO' });
            setShowSpotlight(false);
          }}
        />
      )}
    </div>
  );
};

export default HomeDashboard;
